import React, { useState, useEffect } from "react";
import { AppBrand } from "../../../types/appBrand";
import { fetchAppBrands } from "../../../services/appBrandService";
import styles from "./AppBrandSelector.module.scss";

interface AppBrandSelectorProps {
  onBrandChange: (brand: AppBrand) => void;
  restrictToApotekaRS?: boolean; // For Web ads
  className?: string;
}

const AppBrandSelector: React.FC<AppBrandSelectorProps> = ({
  onBrandChange,
  restrictToApotekaRS = false,
  className,
}) => {
  const [brands, setBrands] = useState<AppBrand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<AppBrand | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadBrands = async () => {
      try {
        setLoading(true);
        const fetchedBrands = await fetchAppBrands();

        // Filter for Web ads if restricted
        const filteredBrands = restrictToApotekaRS
          ? fetchedBrands.filter((brand) => brand.id === 1) // Only Apoteka RS
          : fetchedBrands;

        setBrands(filteredBrands);

        // Set default to Apoteka RS (ID=1) or first available
        const defaultBrand =
          filteredBrands.find((b) => b.id === 1) || filteredBrands[0];
        if (defaultBrand) {
          setSelectedBrand(defaultBrand);
          onBrandChange(defaultBrand);
        }
      } catch (err) {
        setError("Failed to load App Brands");
      } finally {
        setLoading(false);
      }
    };

    loadBrands();
  }, [restrictToApotekaRS]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const brandId = parseInt(event.target.value);
    const brand = brands.find((b) => b.id === brandId);
    if (brand) {
      setSelectedBrand(brand);
      onBrandChange(brand);
    }
  };

  if (loading) return <div className={styles.Loading}>Loading...</div>;
  if (error) return <div className={styles.Error}>{error}</div>;

  return (
    <div className={`${styles.AppBrandSelector} ${className}`}>
      <label htmlFor="app-brand">App Brand:</label>
      <select
        id="app-brand"
        value={selectedBrand?.id || ""}
        onChange={handleChange}
        className={styles.Select}
      >
        {brands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {brand.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default AppBrandSelector;
